import {useRef, type CSSProperties, type ReactElement, type ReactNode} from 'react';

import {Button} from '../../button/button';
import {colors} from '../../colors/colors';
import {CheckIcon, ChevronDownIcon, ErrorCircleIcon, InfoIcon, WarningIcon} from '../../icon';
import type {MessageType} from '../../message/message';
import {Popover} from '../../popover/popover';
import type {LocationEnum} from '../../popover/popover';
import {StatusHeader} from '../../status-button/status-header';

import styles from './dynamic-status-button.module.css';

export type Props = {
    text: string;
    popoverContent?: ReactNode;
    popoverLocation?: LocationEnum;
    headerType: MessageType;
    headerContent?: ReactElement<any>;
    isDialogOpen: boolean;
    setIsDialogOpen: (isDialogOpen: boolean) => void;
};

export function DynamicStatusButton(props: Props) {
    const buttonRef = useRef();

    const getIcon = (): ReactElement<any> => {
        let icon;
        let wrapperStyle: CSSProperties | undefined;

        switch (props.headerType) {
            case 'success':
                wrapperStyle = {color: colors.green};
                icon = <CheckIcon size={15} />;
                break;
            case 'info':
                wrapperStyle = {color: colors.info};
                icon = <InfoIcon size={15} />;
                break;
            case 'error':
                wrapperStyle = {color: colors.red};
                icon = <ErrorCircleIcon size={15} />;
                break;
            case 'warning':
            default:
                wrapperStyle = {color: colors.orangeAttention};
                icon = <WarningIcon size={15} />;
                break;
        }

        return (
            <div className={styles['status-button-icon']} style={wrapperStyle}>
                {icon}
            </div>
        );
    };

    return (
        <>
            <Button
                variant='secondary'
                size='big'
                getButtonRef={buttonRef}
                onClick={() => props.setIsDialogOpen(true)}
            >
                <div className={styles['status-button-container']}>
                    {getIcon()}
                    <div className='mr-8'>{props.text}</div>
                    <ChevronDownIcon />
                </div>
            </Button>
            {props.isDialogOpen ? (
                <Popover
                    anchor={buttonRef.current}
                    onBlur={() => props.setIsDialogOpen(false)}
                    location={props.popoverLocation || 'bottom'}
                    // @ts-expect-error
                    displayUnderModal={true}
                >
                    {props.headerContent ? (
                        <div className={styles['status-popover']}>
                            <StatusHeader
                                type={props.headerType}
                                icon={getIcon()}
                                content={props.headerContent}
                            />
                            {props.popoverContent}
                        </div>
                    ) : (
                        props.popoverContent
                    )}
                </Popover>
            ) : undefined}
        </>
    );
}
